import { Input, Select, Stack } from '@cast/design-system';
import {
  constraintsByOption,
  DayState,
  FormState,
  HourState,
  MinuteState,
  MonthState,
  Option,
  weekDays,
  Weekday,
  WeekState,
  ValidFormState,
} from '@cast/split-me/components/schedules/ScheduleTabs';
import { notReachable } from '@cast/utils';

const scheduleOptions: Option[] = ['Week', 'Day', 'Hour', 'Minute', 'Month'];
const monthDays = Array.from({ length: 31 }, (_, i) => String(i + 1));

type AtFieldsProps<TFormState extends FormState> = {
  formState: TFormState;
  onChange: (formState: TFormState) => void;
};

const WeekAtFields = ({ formState, onChange }: AtFieldsProps<WeekState>) => (
  <Stack direction="row" gap={8} alignItems="flex-end">
    <Select<Weekday, Weekday, 'multiple'>
      inputProps={{ label: 'At' }}
      options={weekDays}
      value={formState.on}
      checkable
      multiple
      inputText={formState.on.length ? 'Selected days' : 'Select days'}
      onChange={(selectedWeekDays) =>
        onChange({ ...formState, on: selectedWeekDays })
      }
      // Options are already in calendar order; the default label sort
      // would list them alphabetically
      applySortingWhileSearching={false}
      size="sm"
    />
    <Input
      type="time"
      label=" "
      value={formState.at}
      onChange={(_event, value) => onChange({ ...formState, at: value })}
      sx={{ width: 90 }}
      size="small"
    />
  </Stack>
);

const MonthAtFields = ({ formState, onChange }: AtFieldsProps<MonthState>) => (
  <Stack direction="row" gap={8} alignItems="flex-end">
    <Select<string, string, 'multiple'>
      inputProps={{ label: 'On' }}
      options={monthDays}
      value={formState.onDay}
      checkable
      multiple
      inputText={formState.onDay.length ? 'Selected days' : 'Select days'}
      onChange={(selectedMonthDays: string[]) =>
        onChange({ ...formState, onDay: selectedMonthDays })
      }
      // Days are numeric strings, so the default label sort would order
      // them 1, 10, 11, ... 19, 2, 20 instead of numerically.
      applySortingWhileSearching={false}
      size="sm"
    />
    <Input
      type="time"
      label="At"
      value={formState.at}
      onChange={(_event, value) => onChange({ ...formState, at: value })}
      sx={{ width: 90 }}
      size="small"
    />
  </Stack>
);

const DayAtFields = ({ formState, onChange }: AtFieldsProps<DayState>) => (
  <Input
    type="time"
    label="At"
    value={formState.at}
    onChange={(_event, value) => onChange({ ...formState, at: value })}
    sx={{ maxWidth: 120 }}
    size="small"
  />
);

const HourAtFields = ({ formState, onChange }: AtFieldsProps<HourState>) => (
  <Input
    type="number"
    label="Start at minute"
    min={0}
    max={59}
    value={formState.startAtMinute}
    onChange={(_event, value: string | number) =>
      onChange({ ...formState, startAtMinute: String(value) })
    }
    sx={{ maxWidth: 120 }}
    size="small"
  />
);

const MinuteAtFields = ({
  formState,
  onChange,
}: AtFieldsProps<MinuteState>) => (
  <Input
    type="time"
    label="Start at"
    value={formState.startAt}
    onChange={(_event, value) => onChange({ ...formState, startAt: value })}
    sx={{ maxWidth: 120 }}
    size="small"
  />
);

const AtFields = ({ formState, onChange }: AtFieldsProps<ValidFormState>) => {
  switch (formState.type) {
    case 'Week':
      return <WeekAtFields formState={formState} onChange={onChange} />;
    case 'Month':
      return <MonthAtFields formState={formState} onChange={onChange} />;
    case 'Day':
      return <DayAtFields formState={formState} onChange={onChange} />;
    case 'Hour':
      return <HourAtFields formState={formState} onChange={onChange} />;
    case 'Minute':
      return <MinuteAtFields formState={formState} onChange={onChange} />;
    default:
      return notReachable(formState);
  }
};

type ScheduleFrequencyFieldsProps = {
  formState: FormState;
  onChange: (formState: FormState) => void;
};

export const ScheduleFrequencyFields = ({
  formState,
  onChange,
}: ScheduleFrequencyFieldsProps) => {
  // `--` models an unparseable cron so it cannot be edited.
  //  Should not be possible since default cron used in optimization wizard is valid
  if (formState.type === '--') {
    return null;
  }

  return (
    <Stack direction="row" gap={8} alignItems="flex-end">
      <Input
        type="number"
        label="Repeat every"
        value={formState.repeatEvery}
        onChange={(_event, value) => {
          // 'Week' always repeats every 1 week, so its interval input is
          // rendered read-only and shouldn't emit changes
          if (formState.type === 'Week') {
            return;
          }

          onChange({ ...formState, repeatEvery: Number(value) });
        }}
        {...constraintsByOption[formState.type].inputProps}
        sx={{ width: 70 }}
        size="small"
      />
      <Select<Option>
        options={scheduleOptions}
        value={formState.type}
        // Switching unit replaces the whole state: the fields of the
        // previous variant do not exist on the new one.
        onChange={(selectedOption: Option) =>
          onChange(constraintsByOption[selectedOption].formState)
        }
        sx={{ width: 96 }}
        size="sm"
      />
      <AtFields formState={formState} onChange={onChange} />
    </Stack>
  );
};
